/**
 * NoticeBlock — bloc colorat cu variante info/success/warning/danger.
 *
 * Varianta se schimbă in-line: header cu 4 pastile, click pe oricare
 * updatează atributul `variant` prin `updateAttributes`. Conținutul
 * editabil rămâne intact (NodeViewContent).
 *
 * Ieșire din bloc:
 * - Mod-Enter (Ctrl/⌘+Enter) oriunde în bloc → paragraf nou după
 * - buton „↵ ieșire" în header (click = același lucru)
 * - Enter pe paragraf gol la finalul blocului → exit automat
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node, mergeAttributes, type Editor } from '@tiptap/core';
import type { ResolvedPos } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';

export type NoticeVariant = 'info' | 'success' | 'warning' | 'danger';

export const NoticeBlockNode = Node.create({
  name: 'noticeBlock',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      variant: {
        default: 'info',
        parseHTML: (el) =>
          (el.getAttribute('data-variant') as NoticeVariant) || 'info',
        renderHTML: (attrs) => ({ 'data-variant': attrs.variant }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-notice]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const variant = (node.attrs.variant as NoticeVariant) || 'info';
    const colorClass = noticeClassFor(variant);
    return [
      'div',
      mergeAttributes(
        { 'data-notice': '', 'data-variant': variant, class: colorClass },
        HTMLAttributes,
      ),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(NoticeView);
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Enter': () => exitNoticeBlock(this.editor),
      Enter: () => {
        // Enter pe paragraf gol la ultimul child → exit
        const { $from, empty } = this.editor.state.selection;
        if (!empty) return false;
        const para = $from.parent;
        if (para.type.name !== 'paragraph' || para.content.size !== 0) return false;
        const depth = findNoticeDepth(this.editor, $from);
        if (depth < 0) return false;
        const parent = $from.node(depth);
        const indexInParent = $from.index(depth);
        if (indexInParent !== parent.childCount - 1) return false;
        // Șterge paragraful gol și adaugă unul după bloc
        const paraStart = $from.before($from.depth);
        const paraEnd = $from.after($from.depth);
        const { tr, schema } = this.editor.state;
        tr.delete(paraStart, paraEnd);
        const insertPos = paraStart;
        tr.insert(insertPos, schema.nodes.paragraph.create());
        tr.setSelection(TextSelection.near(tr.doc.resolve(insertPos + 1)));
        this.editor.view.dispatch(tr);
        return true;
      },
    };
  },
});

function findNoticeDepth(_editor: Editor, $from: ResolvedPos): number {
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type.name === 'noticeBlock') return d;
  }
  return -1;
}

export function exitNoticeBlock(editor: Editor): boolean {
  const { $from } = editor.state.selection;
  const depth = findNoticeDepth(editor, $from);
  if (depth < 0) return false;
  const after = $from.after(depth);
  editor
    .chain()
    .focus()
    .insertContentAt(after, { type: 'paragraph' })
    .setTextSelection(after + 1)
    .run();
  return true;
}

export function noticeClassFor(variant: NoticeVariant): string {
  // Full rounded cu border subtil colorat + tint bg matching variant.
  const base = 'border px-4 py-3 rounded-xl my-3';
  switch (variant) {
    case 'success':
      return `${base} border-green-300 dark:border-green-700/60 bg-green-50 dark:bg-green-900/20`;
    case 'warning':
      return `${base} border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20`;
    case 'danger':
      return `${base} border-red-300 dark:border-red-700/60 bg-red-50 dark:bg-red-900/20`;
    case 'info':
    default:
      return `${base} border-blue-300 dark:border-blue-700/60 bg-blue-50 dark:bg-blue-900/20`;
  }
}

// Iconițe SVG specifice variantelor (mai rich decât text glyph-uri)
function NoticeIcon({ variant }: { variant: NoticeVariant }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className: 'w-5 h-5 flex-shrink-0',
    'aria-hidden': true,
  };
  switch (variant) {
    case 'success':
      return (
        <svg {...common} style={{ color: 'var(--c-mint)' }}>
          <circle cx="12" cy="12" r="10" />
          <polyline points="8,12.5 11,15.5 16,9.5" />
        </svg>
      );
    case 'warning':
      return (
        <svg {...common} style={{ color: 'var(--c-amber)' }}>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'danger':
      return (
        <svg {...common} style={{ color: 'var(--c-rose)' }}>
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    case 'info':
    default:
      return (
        <svg {...common} style={{ color: 'var(--c-blue, var(--accent))' }}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      );
  }
}

// Eticheta afișată vine din i18n: `notes_blocks.notice.<key>`.
const VARIANTS: { key: NoticeVariant; icon: string; pillClass: string }[] = [
  {
    key: 'info',
    icon: 'i',
    pillClass: 'bg-blue-500 text-white',
  },
  {
    key: 'success',
    icon: '✓',
    pillClass: 'bg-green-500 text-white',
  },
  {
    key: 'warning',
    icon: '!',
    pillClass: 'bg-amber-500 text-white',
  },
  {
    key: 'danger',
    icon: '×',
    pillClass: 'bg-red-500 text-white',
  },
];

function NoticeView({ node, updateAttributes, editor }: NodeViewProps) {
  const { t } = useTranslation();
  const current = (node.attrs.variant as NoticeVariant) || 'info';
  const editable = editor.isEditable;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);

  // Toolbar apare doar când clicki în bloc; dispare când clicki în altă
  // parte. Folosim mousedown pe document ca să fie responsive și să prindă
  // click în afară corect.
  useEffect(() => {
    if (!editable) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      setFocused(wrapperRef.current.contains(e.target as globalThis.Node));
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [editable]);

  return (
    <NodeViewWrapper ref={wrapperRef} className={noticeClassFor(current)}>
      <div className="flex items-start gap-3">
        {/* Iconiță permanent vizibilă — identificatorul variantei */}
        <div contentEditable={false} className="pt-0.5 select-none">
          <NoticeIcon variant={current} />
        </div>

        {/* Conținut + toolbar (doar la focus) */}
        <div className="flex-1 min-w-0">
          {editable && focused && (
            <div
              contentEditable={false}
              className="flex items-center gap-1 mb-2 select-none flex-wrap"
            >
              {VARIANTS.map((v) => {
                const active = v.key === current;
                const label = t(`notes_blocks.notice.${v.key}`);
                return (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => updateAttributes({ variant: v.key })}
                    className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 transition-opacity ${
                      active
                        ? v.pillClass
                        : 'bg-[var(--bg-elevated)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]'
                    }`}
                    title={t('notes_blocks.notice.switch_to', { label })}
                  >
                    <span
                      className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[9px] font-bold ${
                        active ? 'bg-white/20' : v.pillClass
                      }`}
                      aria-hidden
                    >
                      {v.icon}
                    </span>
                    {label}
                  </button>
                );
              })}
              <span className="flex-1" />
              <button
                type="button"
                onClick={() => exitNoticeBlock(editor)}
                className="inline-flex items-center gap-1 text-[10px] font-medium text-[var(--text-muted)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)] rounded px-2 py-0.5 border border-[var(--border-strong)]"
                title={t('notes_blocks.notice.exit_title')}
              >
                {t('notes_blocks.notice.exit')}
              </button>
            </div>
          )}
          <NodeViewContent className="notice-content" />
        </div>
      </div>
    </NodeViewWrapper>
  );
}
